import React, { useState, useEffect } from 'react';
import {
  Mic,
  MicOff,
  Volume2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Sparkles,
  Trophy,
  RotateCcw,
  Send,
  Loader2
} from 'lucide-react';
import { SessionGameRound, SessionPlayerState } from '../../../types/session-games';
import { calculatePronunciationScore } from '../../../services/sessionGameService';
import { useSpeechRecognition } from '../../../hooks/useSpeechRecognition';
import { useSpeechSynthesis } from '../../../hooks/useSpeechSynthesis';
import { playCorrectSound, playWrongSound } from '../../../utils/soundEffects';

interface PronunciationDuelViewProps {
  round: SessionGameRound;
  myPlayer: SessionPlayerState;
  partnerPlayer?: SessionPlayerState;
  onSubmit: (result: {
    micTranscript: string;
    accuracyScore: number;
    wordDiff: any[];
    points: number;
    isCorrect: boolean;
  }) => void;
  isRoundResult: boolean;
}

export const PronunciationDuelView: React.FC<PronunciationDuelViewProps> = ({
  round,
  myPlayer,
  partnerPlayer,
  onSubmit,
  isRoundResult
}) => {
  const { speak } = useSpeechSynthesis({ lang: 'en-US' });
  const [localTranscript, setLocalTranscript] = useState('');
  const [evalResult, setEvalResult] = useState<{ accuracyScore: number; diff: any[] } | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Speech recognition hook
  const { isRecording, transcript, startRecording, stopRecording } = useSpeechRecognition({
    onSendMessage: (msg) => {
      setLocalTranscript(msg);
    },
    isFromChatInput: false,
    isFromPractice: true
  });

  // Track transcript
  useEffect(() => {
    if (transcript) {
      setLocalTranscript(transcript);
    }
  }, [transcript]);

  // When recording stops or transcript updates, evaluate score
  useEffect(() => {
    if (localTranscript.trim()) {
      const evaluation = calculatePronunciationScore(round.targetText, localTranscript);
      setEvalResult(evaluation);
    }
  }, [localTranscript, round.targetText]);

  // Reset when round changes
  useEffect(() => {
    setLocalTranscript('');
    setEvalResult(null);
    setSubmitted(false);
  }, [round.id]);

  const toggleMic = () => {
    if (submitted || isRoundResult) return;
    if (isRecording) {
      stopRecording();
    } else {
      setLocalTranscript('');
      setEvalResult(null);
      startRecording();
    }
  };

  const handleSubmit = () => {
    if (!evalResult || submitted) return;
    setSubmitted(true);

    const isGood = evalResult.accuracyScore >= 70;
    if (isGood) {
      playCorrectSound();
    } else {
      playWrongSound();
    }

    const earnedPoints = Math.round(evalResult.accuracyScore / 5); // 0-20 points
    onSubmit({
      micTranscript: localTranscript,
      accuracyScore: evalResult.accuracyScore,
      wordDiff: evalResult.diff,
      points: earnedPoints,
      isCorrect: isGood
    });
  };

  return (
    <div className="flex flex-col items-center justify-between h-full max-w-2xl mx-auto space-y-6">
      {/* Target Phrase Card */}
      <div className="w-full bg-white rounded-3xl p-6 md:p-8 border border-purple-100 shadow-md text-center relative overflow-hidden">
        <div className="absolute top-3 left-4 flex items-center gap-1.5 text-xs font-bold text-purple-600 uppercase tracking-wider">
          <Sparkles className="w-4 h-4" />
          <span>Pronounce Clearly</span>
        </div>

        <button
          onClick={() => speak(round.targetText, 'en-US')}
          className="absolute top-3 right-4 p-2 text-purple-600 hover:bg-purple-50 rounded-xl transition-colors flex items-center gap-1 text-xs font-semibold"
          title="Listen to native pronunciation"
        >
          <Volume2 className="w-4 h-4" />
          <span>Listen</span>
        </button>

        <div className="mt-6 mb-3">
          <p className="text-xl md:text-2xl font-black text-gray-900 leading-snug tracking-tight">
            "{round.targetText}"
          </p>
        </div>

        {round.translation && (
          <p className="text-xs md:text-sm text-purple-700 font-medium italic">
            {round.translation}
          </p>
        )}
      </div>

      {/* Mic Recording Area */}
      <div className="flex flex-col items-center space-y-3">
        <button
          onClick={toggleMic}
          disabled={submitted || isRoundResult}
          className={`w-20 h-20 rounded-full flex items-center justify-center shadow-lg transition-all transform active:scale-95 ${
            isRecording
              ? 'bg-red-500 text-white ring-8 ring-red-200 animate-pulse scale-105'
              : submitted
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-tr from-purple-600 to-indigo-600 text-white hover:scale-105 shadow-purple-200'
          }`}
        >
          {isRecording ? <MicOff className="w-8 h-8" /> : <Mic className="w-8 h-8" />}
        </button>

        <p className="text-xs font-bold text-gray-500">
          {isRecording
            ? 'Listening... Speak now!'
            : submitted
            ? 'Score submitted! Waiting for next round...'
            : 'Tap mic and read the phrase aloud'}
        </p>
      </div>

      {/* Live Transcript & Phonetic Comparison */}
      {localTranscript && (
        <div className="w-full bg-gray-50 rounded-2xl p-4 md:p-5 border border-gray-200/80 space-y-3">
          <div className="flex items-center justify-between border-b border-gray-200 pb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
              Your Pronunciation Feedback:
            </span>
            {evalResult && (
              <span
                className={`text-xs font-black px-2.5 py-0.5 rounded-full ${
                  evalResult.accuracyScore >= 80
                    ? 'bg-emerald-100 text-emerald-800'
                    : evalResult.accuracyScore >= 50
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {evalResult.accuracyScore}% Accuracy
              </span>
            )}
          </div>

          {/* Word-by-word visualizer */}
          {evalResult && (
            <div className="flex flex-wrap gap-2 justify-center py-2">
              {evalResult.diff.map((item, idx) => (
                <div
                  key={idx}
                  className={`px-3 py-1.5 rounded-xl text-sm font-bold flex flex-col items-center border ${
                    item.status === 'correct'
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                      : item.status === 'close'
                      ? 'bg-amber-50 text-amber-800 border-amber-200'
                      : 'bg-red-50 text-red-800 border-red-200'
                  }`}
                >
                  <span>{item.original}</span>
                  <span className="text-[10px] font-normal opacity-75">
                    {item.spoken || '(missed)'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Spoken raw transcript */}
          <p className="text-xs text-gray-400 italic text-center">
            Heard: "{localTranscript}"
          </p>
        </div>
      )}

      {/* Submit / Action Buttons */}
      {!submitted && !isRoundResult && evalResult && (
        <button
          onClick={handleSubmit}
          className="w-full max-w-sm py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-2xl font-black text-sm shadow-md hover:opacity-95 transition-all flex items-center justify-center gap-2"
        >
          <Send className="w-4 h-4" />
          <span>Confirm & Submit Score ({Math.round(evalResult.accuracyScore / 5)} pts)</span>
        </button>
      )}

      {/* Partner Status Card */}
      {partnerPlayer && (
        <div className="w-full bg-white rounded-2xl p-3 border border-gray-100 shadow-xs flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <span className="font-bold text-gray-800">{partnerPlayer.userName}:</span>
            <span className="text-gray-500">
              {partnerPlayer.roundFinished ? 'Finished speaking' : 'Recording / Thinking...'}
            </span>
          </div>
          {partnerPlayer.roundFinished ? (
            <span className="font-bold text-emerald-600 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              {partnerPlayer.accuracyScore !== undefined ? `${partnerPlayer.accuracyScore}%` : 'Done'}
            </span>
          ) : (
            <span className="text-amber-500 flex items-center gap-1">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              In Progress
            </span>
          )}
        </div>
      )}
    </div>
  );
};
